// Sequential orchestration of the existing single-league workflow. No settings formula.
const clone = (value) => JSON.parse(JSON.stringify(value));
export function summarizeDraft(context, draft) {
  const changes = draft.preview.rows.map((row) => {
    const before = context.teams.find((t) => t.team_id === row.team_id).setting;
    const strength = row.total_strength / before.total_strength - 1;
    const goals =
      (row.for_total + row.against_total) /
        (before.for_total + before.against_total) -
      1;
    return {
      team_id: row.team_id,
      strength,
      goals,
      fields: ["for_home", "against_home", "for_away", "against_away"].filter(
        (k) => Math.abs(row[k] - before[k]) > 1e-10
      ).length,
    };
  });
  const largest = (key) =>
    changes.reduce(
      (best, t) => (Math.abs(t[key]) > Math.abs(best[key]) ? t : best),
      { [key]: 0 }
    );
  return {
    teams: changes.length,
    changed_teams: changes.filter((t) => t.fields).length,
    changed_fields: changes.reduce((n, t) => n + t.fields, 0),
    largest_strength: largest("strength"),
    largest_goals: largest("goals"),
    outliers: changes.filter(
      (t) => Math.abs(t.strength) > 0.25 || Math.abs(t.goals) > 0.1
    ).length,
    parameters: Object.fromEntries(
      ["stored_goal_average", "exp_home_factor", "drawfactor"].map((k) => [
        k,
        {
          before: context.parameters[k],
          after: draft.preview.parameters[k],
          delta: draft.preview.parameters[k] - context.parameters[k],
        },
      ])
    ),
  };
}
const scope = (item) => ({
  league_id: item.league_id,
  season_id: item.season_id,
});
const checked = async (request, body) => {
  const r = await request(body);
  if (!r || r.success === false) throw Error(r?.code || "REQUEST_FAILED");
  return r;
};
const emit = (run, progress) => progress?.(clone(run));
export async function analyzeLeagueBatch(
  catalog,
  request,
  { id, profile, market_orientation, language, skipSerieA = true },
  progress
) {
  const run = {
    id,
    profile,
    market_orientation,
    language,
    created_at: new Date().toISOString(),
    items: [],
    phase: "analyzing",
    completed: 0,
    total: catalog.reduce(
      (n, l) => n + l.seasons.filter((s) => s.is_current !== false).length,
      0
    ),
  };
  emit(run, progress);
  for (const league of catalog)
    for (const season of league.seasons.filter((s) => s.is_current !== false)) {
      const item = {
        league_id: league.league_id,
        league_name: league.league_name,
        season_id: season.id,
        status: "pending",
      };
      run.items.push(item);
      try {
        if (skipSerieA && Number(item.league_id) === 384) {
          item.status = "skipped";
          item.error = "SERIE_A_BASELINE_NOT_VERIFIED";
        } else {
          item.context = (
            await checked(request, { mode: "context", ...scope(item) })
          ).context;
          item.draft = await checked(request, {
            mode: "batch-analyze",
            ...scope(item),
            revision: item.context.revision,
            profile,
            market_orientation,
            language,
          });
          item.summary = summarizeDraft(item.context, item.draft);
          item.status = "ready";
        }
      } catch (e) {
        item.error = e.message;
        item.status =
          e.message === "OPENAI_REQUIRED_NOT_CALLED" ? "blocked" : "error";
      }
      run.completed++;
      emit(run, progress);
    }
  run.phase = "review";
  emit(run, progress);
  return run;
}
export async function saveLeagueBatch(original, request, selection, progress) {
  const run = clone(original),
    items = run.items.filter(
      (i) =>
        i.status === "ready" &&
        (selection === "all" ||
          (selection === "unremarkable" && i.summary.outliers === 0) ||
          (Array.isArray(selection) && selection.includes(i.league_id)))
    );
  if (!items.length) return { ...run, phase: "no_drafts_to_save" };
  // Preflight all selected scopes before the first write. Per-league atomicity remains authoritative.
  for (const item of items) {
    const current = (
      await checked(request, { mode: "context", ...scope(item) })
    ).context;
    if (current.revision !== item.draft.revision) throw Error("STALE_DRAFT");
    if (!current.atomic_save_available) throw Error("ATOMIC_STORE_UNAVAILABLE");
  }
  run.phase = "saving";
  emit(run, progress);
  for (const item of items) {
    try {
      const r = await checked(request, {
        mode: "batch-save",
        ...scope(item),
        batch_id: run.id,
        run_id: `${run.id}-${item.season_id}`,
        revision: item.draft.revision,
        team_targets: item.draft.team_targets,
        drawfactor: item.draft.drawfactor,
        confirm: true,
      });
      item.saved = r;
      item.status = r.saved === false ? "unchanged" : "saved";
      if (r.snapshot) {
        item.snapshot = await checked(request, {
          mode: "batch-snapshot-check",
          ...scope(item),
          batch_id: run.id,
          restore_id: r.snapshot.id,
        });
        if (!item.snapshot.complete) throw Error("INCOMPLETE_BACKUP");
      }
    } catch (e) {
      item.error = e.message;
      run.phase = "partial-save";
      emit(run, progress);
      return run;
    }
    emit(run, progress);
  }
  run.phase = "saved";
  emit(run, progress);
  return run;
}
export async function resetLeagueBatch(original, request, progress) {
  const run = clone(original),
    items = run.items.filter(
      (i) => i.saved?.snapshot && i.status !== "restored"
    );
  if (!items.length) throw Error("NO_BATCH_SNAPSHOTS");
  for (const item of items) {
    item.snapshot = await checked(request, {
      mode: "batch-snapshot-check",
      ...scope(item),
      batch_id: run.id,
      restore_id: item.saved.snapshot.id,
    });
    if (
      !item.snapshot.complete ||
      (!item.snapshot.can_restore && !item.snapshot.already_restored)
    )
      throw Error("BATCH_CURRENT_CHANGED");
  }
  run.phase = "restoring";
  emit(run, progress);
  for (const item of items) {
    try {
      if (!item.snapshot.already_restored)
        await checked(request, {
          mode: "batch-restore",
          ...scope(item),
          batch_id: run.id,
          restore_id: item.saved.snapshot.id,
          revision: item.snapshot.revision,
          run_id: `reset-${run.id}-${item.season_id}`,
          confirm: true,
        });
      const verify = await checked(request, {
        mode: "batch-snapshot-check",
        ...scope(item),
        batch_id: run.id,
        restore_id: item.saved.snapshot.id,
      });
      if (!verify.already_restored) throw Error("RESET_VERIFICATION_FAILED");
      item.status = "restored";
    } catch (e) {
      item.error = e.message;
      run.phase = "partial-reset";
      emit(run, progress);
      return run;
    }
    emit(run, progress);
  }
  run.phase = "restored";
  emit(run, progress);
  return run;
}
export async function recoverLeagueBatch(catalog, request) {
  const found = [];
  for (const l of catalog)
    for (const s of l.seasons.filter((s) => s.is_current !== false)) {
      const r = await checked(request, {
        mode: "batch-revisions",
        league_id: l.league_id,
        season_id: s.id,
      });
      for (const rev of r.revisions || [])
        if (rev.batch_id && rev.kind === "commit")
          found.push({
            league_id: l.league_id,
            league_name: l.league_name,
            season_id: s.id,
            rev,
          });
    }
  if (!found.length) throw Error("NO_BATCH_SNAPSHOTS");
  found.sort((a, b) => b.rev.at.localeCompare(a.rev.at));
  const id = found[0].rev.batch_id,
    items = [];
  for (const f of found.filter((f) => f.rev.batch_id === id)) {
    const snapshot = await checked(request, {
      mode: "batch-snapshot-check",
      ...scope(f),
      batch_id: id,
      restore_id: f.rev.id,
    });
    items.push({
      ...scope(f),
      league_name: f.league_name,
      saved: { snapshot: { id: f.rev.id }, revision: f.rev.after_revision },
      snapshot,
      status: snapshot.already_restored ? "restored" : "saved",
    });
  }
  return { id, items, phase: "recovered" };
}
